<template>
  <v-row class="ma-0 pa-0 pl-4 mt-2 align-center">
    <v-btn v-if="showBackButton" @click="previous" class="mr-3" variant="text">
      Back
    </v-btn>
    <OareLoaderButton
      :disabled="blockContinue"
      :loading="continueLoading"
      color="primary"
      @click="next"
    >
      {{ continueError ? 'Retry' : continueButtonText }}
    </OareLoaderButton>
    <span v-if="blockContinue" class="text-red ml-4">{{
      blockContinueText
    }}</span>
    <span v-else-if="continueError" class="text-red ml-4 test-continue-error"
      >{{ continueError }}
      <a
        v-if="continueErrorLink"
        :href="continueErrorLink.href"
        target="_blank"
        rel="noopener"
        class="test-continue-error-link"
        >{{ continueErrorLink.text }}</a
      ></span
    >
  </v-row>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import sl from '@/serviceLocator';

// A continue action can throw an error with a link for the user to follow.
export interface StepError extends Error {
  link?: { href: string; text: string };
}

export default defineComponent({
  props: {
    showBackButton: {
      type: Boolean,
      default: true,
    },
    continueButtonText: {
      type: String,
      default: 'Continue',
    },
    blockContinue: {
      type: Boolean,
      default: false,
    },
    blockContinueText: {
      type: String,
      required: false,
    },
    continueAction: {
      type: Function as PropType<() => Promise<void>>,
      required: false,
    },
    continueErrorText: {
      type: String,
      default: 'Error continuing to next step. Please try again.',
    },
  },
  emits: ['previous', 'next'],
  setup(props, { emit }) {
    const actions = sl.get('globalActions');

    const continueLoading = ref(false);
    // Set when the continue action failed: the step stays put and the
    // button becomes Retry.
    const continueError = ref('');
    const continueErrorLink = ref<StepError['link']>();

    const previous = () => {
      continueError.value = '';
      emit('previous');
    };

    const next = async () => {
      continueLoading.value = true;
      try {
        if (props.continueAction) {
          await props.continueAction();
        }
      } catch (err) {
        // The axios interceptor rejects with the server's message. An error
        // may also carry a link the user should check before retrying.
        const { message, link } = err as StepError;
        continueError.value = message
          ? `${props.continueErrorText} ${message}`
          : props.continueErrorText;
        continueErrorLink.value = link;
        actions.showErrorSnackbar(props.continueErrorText, err as Error);
        return;
      } finally {
        continueLoading.value = false;
      }
      continueError.value = '';
      continueErrorLink.value = undefined;
      emit('next');
    };

    return {
      previous,
      next,
      continueLoading,
      continueError,
      continueErrorLink,
    };
  },
});
</script>
